import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal, { Eyebrow } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import Icon from "../components/Icon";
import { STATES, SERVICES } from "../data/siteData";

export default function StatePage() {
  const { stateSlug } = useParams();
  const state = STATES.find((s) => s.slug === stateSlug);
  if (!state) return <Navigate to="/locations" replace />;

  return (
    <>
      <SEO
        title={`CA & CS Services in ${state.name} — Company Registration, Virtual CFO & Fundraising | Complisure`}
        description={`Top CA CS firm in ${state.name}: company registration, GST, ROC compliance, Virtual CFO, business valuation and fundraising advisory across ${state.cities.map((c) => c.name).join(", ")}.`}
        canonical={`https://compliance-capital-1.preview.emergentagent.com/locations/${state.slug}`}
        jsonLd={[
          orgJsonLd,
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: state.name, path: `/locations/${state.slug}` },
          ]),
        ]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float right-[-6%] top-[-8%] h-96 w-96 bg-brand-blue/10" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <nav className="flex items-center gap-1.5 text-xs text-brand-gray" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brand-blue">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/locations" className="hover:text-brand-blue">Locations</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-brand-navy">{state.name}</span>
            </nav>
            <div className="mt-6">
              <Eyebrow>{state.tagline}</Eyebrow>
              <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold tracking-[-0.02em] text-brand-navy sm:text-5xl">
                CA, CS & Legal advisory in <span className="text-gradient">{state.name}</span>
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-gray sm:text-lg">
                From incorporation to growth-stage fundraising rounds — {state.cities.length} city desks across {state.name}, one national standard of work.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {state.cities.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.08}>
                <Link to={`/locations/${state.slug}/${c.slug}`} data-testid={`state-city-card-${c.slug}`} className="glass card-lift group flex h-full flex-col rounded-3xl p-7">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy group-hover:text-brand-blue">{c.name}</h2>
                    <span className="rounded-full bg-brand-ice px-2.5 py-1 text-[10px] font-semibold text-brand-blue">{c.tier}</span>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-gray line-clamp-3">{c.eco}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue transition-all group-hover:gap-3">
                    CA services in {c.name} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy">What we do in {state.name}</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {SERVICES.slice(0, 8).map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="glass card-lift flex items-start gap-3 rounded-2xl p-4">
                    <Icon name={s.icon} className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
                    <span className="text-sm font-medium text-brand-navy">{s.name}</span>
                  </Link>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <LeadForm city={state.cities[0].name} sourcePage={`/locations/${state.slug}`} />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
